/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import StaffComponent from '../components/Staff/Staff'
import SearchBar from '../components/TableHeader/SearchBar'
import { getStaffs, deleteStaff, editStaff } from '../apollo'
import useGlobalStyles from '../utils/globalStyles'
import { Container, Modal, Switch, Box, Button } from '@mui/material'
import { toneTile } from '../utils/depth'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu, ConfirmDialog } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import TableHeader from '../components/TableHeader'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_STAFFS = gql`
  ${getStaffs}
`
const DELETE_STAFF = gql`
  ${deleteStaff}
`
const EDIT_STAFF = gql`
  ${editStaff}
`

const PersonCell = ({ name, sub }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '10px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 800,
        ...toneTile('orange')
      }}>
      {(name || '?').trim().charAt(0).toUpperCase()}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ fontWeight: 600, color: 'text.primary' }}>{name}</Box>
      {sub ? (
        <Box sx={{ fontSize: 12, color: 'text.disabled' }}>{sub}</Box>
      ) : null}
    </Box>
  </Box>
)

function Staff(props) {
  const [editModal, setEditModal] = useState(false)
  const [staff, setStaff] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteId, setDeleteId] = useState(null)
  const onChangeSearch = e => setSearchQuery(e.target.value)

  const [mutateToggle] = useMutation(EDIT_STAFF, {
    refetchQueries: [{ query: GET_STAFFS }]
  })
  const [mutateDelete] = useMutation(DELETE_STAFF, {
    refetchQueries: [{ query: GET_STAFFS }]
  })

  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_STAFFS
  )

  const toggleModal = selected => {
    setEditModal(!editModal)
    setStaff(selected || null)
  }

  const closeEditModal = () => {
    setEditModal(false)
  }

  const closeDeleteDialog = () => {
    setDeleteId(null)
  }

  const confirmDelete = () => {
    if (!deleteId) return
    mutateDelete({
      variables: { id: deleteId },
      onCompleted: () => closeDeleteDialog(),
      onError: () => closeDeleteDialog()
    })
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const handleSort = (column, sortDirection) =>
    console.log(column.selector, sortDirection)

  const { t } = props

  const columns = [
    {
      name: t('Name'),
      sortable: true,
      selector: 'name',
      minWidth: '200px',
      cell: row => <PersonCell name={row.name} sub={row.email} />
    },
    {
      name: t('Email'),
      sortable: true,
      selector: 'email'
    },
    {
      name: t('Password'),
      sortable: true,
      selector: row => row.plainPassword || row.password
    },
    {
      name: t('Phone'),
      sortable: true,
      selector: row => row.phone
    },
    // {
    //   name: t('Status'),
    //   cell: row => <>{statusToggle(row)}</>
    // },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]

  const statusToggle = row => {
    return (
      <Switch
        size="small"
        checked={row.isActive}
        onChange={_event => {
          mutateToggle({
            variables: {
              staffInput: {
                _id: row._id,
                name: row.name,
                email: row.email,
                password: row.plainPassword || row.password,
                phone: row.phone,
                isActive: !row.isActive,
                permissions: row.permissions || []
              }
            }
          })
        }}
        style={{ color: 'black' }}
      />
    )
  }

  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  toggleModal(row)
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                confirm: false,
                divider: true,
                onClick: () => {
                  setDeleteId(row._id)
                }
              }
            ]}
          />
        </div>
      </>
    )
  }

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.staffs
      : data &&
        data.staffs.filter(staffMember => {
          return (
            staffMember.name.toLowerCase().search(regex) > -1 ||
            (staffMember.email
              ? staffMember.email.toLowerCase().search(regex) > -1
              : false) ||
            String(staffMember.phone).toLowerCase().search(regex) > -1
          )
        })

  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => toggleModal(null)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Staff')}`}
          </Button>
        }
      />
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? (
          <tr>
            <td>`Error! ${errorQuery.message}`</td>
          </tr>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('Staff')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            onSort={handleSort}
            sortFunction={customSort}
            customStyles={customStyles}
          />
        )}
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal()
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <StaffComponent staff={staff} onClose={closeEditModal} />
          </Box>
        </Modal>
        <ConfirmDialog
          open={Boolean(deleteId)}
          title={`${t('Delete')} ${t('Staff')}?`}
          message={t('DeleteConfirm')}
          confirmLabel={t('Delete')}
          cancelLabel={t('Cancel')}
          onClose={closeDeleteDialog}
          onConfirm={confirmDelete}
        />
      </Container>
    </>
  )
}

export default withTranslation()(Staff)
